import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { hasPermission } from '@/types';
import { Icon } from './Icon';

/**
 * Bramka trasy oparta na macierzy uprawnień. Renderuje dzieci tylko gdy zalogowany
 * użytkownik ma wymagane uprawnienia (`user.permissions`); w przeciwnym razie pokazuje
 * panel „Brak uprawnień". Dzięki temu ukryta w menu strona jest też nieosiągalna przez
 * wpisanie URL — menu (Layout) i trasa (App) używają tego samego źródła prawdy (macierzy),
 * a nie sztywnych ról.
 *
 * `mode` domyślnie 'any' (wystarczy jedno z podanych). `mode="all"` jest potrzebny tam,
 * gdzie API wymaga KONIUNKCJI — np. raport ofert stoi pod `REPORTS_VIEW and FINANCE_VIEW`.
 * Bez tego trybu trasa wpuszczała szerzej niż endpoint i powstawał ekran zwracający 403.
 */
export function RequirePerm({ perm, mode = 'any', children }:
    { perm: string | string[]; mode?: 'any' | 'all'; children: ReactNode }) {
  const { user } = useAuth();
  const perms = Array.isArray(perm) ? perm : [perm];
  const ok = mode === 'all'
    ? perms.every(p => hasPermission(user, p))
    : perms.some(p => hasPermission(user, p));

  if (!ok) {
    return (
      <div className="max-w-md mx-auto mt-16 card p-8 text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-red-100 text-red-500 grid place-items-center mb-4">
          <Icon name="shield" className="w-6 h-6" />
        </div>
        <h2 className="text-ink-900 mb-1">Brak uprawnień</h2>
        <p className="text-[13px] text-ink-500 mb-5">
          Nie masz uprawnień do tej sekcji. Jeśli powinno być inaczej — poproś administratora
          o nadanie uprawnienia w macierzy uprawnień.
        </p>
        <NavLink to="/dashboard" className="btn btn-primary btn-sm">Wróć do pulpitu</NavLink>
      </div>
    );
  }
  return <>{children}</>;
}
